import { useCallback, useEffect, useState } from 'react';
import type { GameState, BetsUpdatePayload } from '@flip7/shared';
import { TABLE_TIERS, EVENTS } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { Avatar } from '../components/Avatar';
import { socket } from '../lib/socket';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface LobbyScreenProps {
  gameState: GameState;
  playerId: string | null;
  isHost: boolean;
  isSpectator: boolean;
  onStartGame: () => void;
  onAddPlayer: (playerName: string) => void;
  onRemovePlayer: (playerId: string) => void;
  onKickSpectator: (spectatorId: string) => void;
  onToggleWantToPlay: () => void;
}

export function LobbyScreen({ gameState, playerId, isHost, isSpectator, onStartGame, onAddPlayer, onRemovePlayer, onKickSpectator, onToggleWantToPlay }: LobbyScreenProps) {
  const { accessToken } = useAuth();
  const [newPlayerName, setNewPlayerName] = useState('');
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [betAmount, setBetAmount] = useState('');
  const [bets, setBets] = useState<Record<string, number>>(gameState.bets ?? {});
  const [pot, setPot] = useState(gameState.pot ?? 0);

  // Listen for bets updates
  useEffect(() => {
    const handler = (data: BetsUpdatePayload) => {
      setBets(data.bets);
      setPot(data.pot);
    };
    socket.on(EVENTS.BETS_UPDATE, handler);
    return () => { socket.off(EVENTS.BETS_UPDATE, handler); };
  }, []);

  // Sync from gameState when it changes
  useEffect(() => {
    setBets(gameState.bets ?? {});
    setPot(gameState.pot ?? 0);
  }, [gameState.bets, gameState.pot]);

  const placeBet = () => {
    const amount = parseInt(betAmount, 10);
    if (isNaN(amount) || amount < 0) return;
    socket.emit(EVENTS.PLACE_BET, { roomCode: gameState.roomCode, amount });
  };

  const myBet = playerId ? (bets[playerId] ?? 0) : 0;

  const shareUrl = gameState.groupSlug
    ? `${window.location.origin}/group/${gameState.groupSlug}`
    : `${window.location.origin}/room/${gameState.roomCode}`;

  const handleAddPlayer = () => {
    if (newPlayerName.trim()) {
      onAddPlayer(newPlayerName.trim());
      setNewPlayerName('');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tierCfg = TABLE_TIERS[gameState.tier];

  return (
    <div className="table-lobby">
      {/* Room code chip */}
      <div className="table-lobby__room-chip">
        <span className="table-lobby__room-code">{gameState.roomCode}</span>
        <div className="table-lobby__room-actions">
          <button className="table-lobby__room-action table-lobby__room-action--secondary" onClick={copyLink}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          {accessToken && (
            <button className="table-lobby__room-action" onClick={() => setShowInvite(true)}>
              Invite
            </button>
          )}
        </div>
      </div>

      {/* Settings chips */}
      <div className="table-lobby__settings">
        <span className="table-lobby__setting-chip table-lobby__setting-chip--active">
          Target {gameState.targetScore}
        </span>
        <span className={`table-lobby__setting-chip${gameState.mode !== 'score-tracker' ? ' table-lobby__setting-chip--active' : ''}`}>
          {gameState.mode === 'digital' ? 'Digital' : gameState.mode === 'hot-streak' ? 'Hot Streak' : 'Tracker'}
        </span>
        {gameState.variant && gameState.variant !== 'standard' && (
          <span className="table-lobby__setting-chip table-lobby__setting-chip--active">
            {gameState.variant === 'vengeance-brutal' ? 'Brutal' : 'Vengeance'}
          </span>
        )}
      </div>

      {/* Tier badge + betting */}
      {gameState.mode === 'digital' && (
        <div className="table-lobby__tier">
          <span
            className="table-lobby__tier-badge"
            style={{
              background: gameState.tier === 'gold' ? 'linear-gradient(135deg, #FFD700, #FFA000)'
                : gameState.tier === 'silver' ? 'linear-gradient(135deg, #C0C0C0, #8A8A8A)'
                : gameState.tier === 'bronze' ? 'linear-gradient(135deg, #CD7F32, #A0522D)'
                : 'var(--cream)',
              color: gameState.tier === 'free' ? 'var(--text-muted)' : 'white',
              border: gameState.tier === 'free' ? '1px solid var(--border-subtle)' : 'none',
            }}
          >
            {gameState.tier === 'free' ? 'Free' : gameState.tier}
          </span>
          <span>
            {tierCfg.entryFee > 0
              ? `Entry: ${tierCfg.entryFee}`
              : `Win: +${tierCfg.winReward}`}
            {pot > 0 && ` \u00B7 Pot: ${pot}`}
          </span>
          {accessToken && !isSpectator && (
            <>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && placeBet()}
                placeholder={myBet > 0 ? `${myBet}` : '0'}
                min={0}
                style={{ width: '50px', fontSize: '0.6rem', padding: '3px 6px', minHeight: 'unset' }}
              />
              <button className="btn btn-secondary btn-small" onClick={placeBet} disabled={!betAmount} style={{ fontSize: '0.55rem', padding: '1px 8px', lineHeight: 1, minHeight: 'unset' }}>
                {myBet > 0 ? 'Update' : 'Bet'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Player seats */}
      <div className="table-lobby__seats">
        {gameState.players.map((player, i) => {
          const playerBet = bets[player.id] ?? 0;
          return (
            <div
              key={player.id}
              className={`table-lobby__seat${player.isHost ? ' table-lobby__seat--host' : ''}`}
              style={{ animationDelay: `${i * 80}ms`, position: 'relative' }}
            >
              <div className={`table-lobby__seat-avatar${player.avatar ? ' table-lobby__seat-avatar--emoji' : ''}`}>
                {player.avatarConfig ? (
                  <Avatar config={player.avatarConfig} emoji={player.avatar} size={36} />
                ) : (
                  player.avatar || player.name.charAt(0).toUpperCase()
                )}
              </div>
              <span className="table-lobby__seat-name">
                {player.name}
                {player.isBot && ' \uD83E\uDD16'}
              </span>
              {player.isHost && <span className="table-lobby__seat-badge">HOST</span>}
              {player.title && (
                <span style={{ fontSize: '0.45rem', color: 'var(--gold-dim)', fontStyle: 'italic' }}>{player.title}</span>
              )}
              {playerBet > 0 && (
                <span style={{ fontSize: '0.45rem', color: 'var(--text-muted)', fontWeight: 600 }}>{playerBet} coins</span>
              )}
              {isHost && !player.isHost && (
                <button
                  className="table-lobby__seat-remove"
                  onClick={() => onRemovePlayer(player.id)}
                >
                  &times;
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add player (host, score-tracker) */}
      {isHost && gameState.mode !== 'digital' && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', width: '100%', maxWidth: '300px', marginBottom: 'var(--space-lg)' }}>
          <input
            type="text"
            value={newPlayerName}
            onChange={(e) => setNewPlayerName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPlayer()}
            placeholder="Add player..."
            maxLength={20}
            style={{ flex: 1, minWidth: 0, fontSize: '0.8rem', padding: '8px 12px', minHeight: 'unset' }}
          />
          <button className="btn btn-secondary btn-small" onClick={handleAddPlayer} disabled={!newPlayerName.trim()}>
            +
          </button>
        </div>
      )}

      {/* Bot warning */}
      {gameState.hasBots && (
        <p style={{ fontSize: '0.6rem', color: 'var(--color-warning)', fontWeight: 600, marginBottom: 'var(--space-md)' }}>
          No coins with bots
        </p>
      )}

      {/* Start / Waiting */}
      {isHost ? (
        <button className="btn btn-primary table-lobby__start" onClick={onStartGame}>
          Start Game
        </button>
      ) : !isSpectator ? (
        <p className="table-lobby__waiting">Waiting for host...</p>
      ) : null}

      {/* Spectator toggle */}
      {isSpectator && (
        <button className="btn btn-secondary btn-small" onClick={onToggleWantToPlay} style={{ marginTop: 'var(--space-md)', fontSize: '0.75rem' }}>
          {gameState.spectators.find(s => s.wantsToPlay) ? 'Cancel \u2014 Stay Spectating' : 'I Want to Play!'}
        </button>
      )}

      {/* Spectator list */}
      {gameState.spectators.length > 0 && (
        <div className="table-lobby__spectators">
          {gameState.spectators.map(s => {
            const queued = gameState.spectators.filter(sp => sp.wantsToPlay).sort((a, b) => (a.queuedAt ?? 0) - (b.queuedAt ?? 0));
            const pos = s.wantsToPlay ? queued.findIndex(sp => sp.id === s.id) + 1 : 0;
            return (
              <span
                key={s.id}
                className="table-lobby__spectator"
                style={{ opacity: s.isConnected ? 1 : 0.4 }}
              >
                {s.name}
                {pos > 0 && ` #${pos}`}
                {isHost && (
                  <button
                    onClick={() => onKickSpectator(s.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--color-bust)', fontSize: '0.6rem', cursor: 'pointer', marginLeft: '4px', padding: 0 }}
                  >
                    &times;
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Invite Friends Modal */}
      {showInvite && accessToken && (
        <InviteFriendsModal
          accessToken={accessToken}
          roomCode={gameState.roomCode}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

interface OnlineFriend {
  id: string;
  name: string;
  activeAvatar: string | null;
}

function InviteFriendsModal({ accessToken, roomCode, onClose }: { accessToken: string; roomCode: string; onClose: () => void }) {
  const [friends, setFriends] = useState<OnlineFriend[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sent, setSent] = useState(false);

  const fetchFriends = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/friends`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) {
      const data = await res.json();
      setFriends(data.map((f: { id: string; name: string; activeAvatar: string | null }) => ({
        id: f.id, name: f.name, activeAvatar: f.activeAvatar,
      })));
    }
    setLoading(false);
  }, [accessToken]);

  useEffect(() => { fetchFriends(); }, [fetchFriends]);

  const toggleFriend = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const sendInvites = () => {
    if (selected.size === 0) return;
    socket.emit(EVENTS.SEND_GAME_INVITE, { targetUserIds: Array.from(selected), roomCode });
    setSent(true);
    setTimeout(onClose, 1500);
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface--raised modal-panel">
        <div className="flex-between">
          <p className="label" style={{ margin: 0 }}>Invite Friends</p>
          <button onClick={onClose} className="btn-close">&times;</button>
        </div>
        {sent ? (
          <p className="modal-message modal-message--success">Invites sent!</p>
        ) : loading ? (
          <p className="modal-message text-muted">Loading...</p>
        ) : friends.length === 0 ? (
          <p className="modal-message text-muted">No friends yet. Add friends from the Social panel!</p>
        ) : (
          <>
            <div className="flex-col" style={{ overflowY: 'auto', gap: '4px' }}>
              {friends.map(f => (
                <button
                  key={f.id}
                  onClick={() => toggleFriend(f.id)}
                  className="player-row"
                  style={{
                    padding: '8px 12px', cursor: 'pointer', width: '100%',
                    textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px',
                    background: selected.has(f.id) ? 'var(--gold-pale)' : undefined,
                    border: selected.has(f.id) ? '1px solid var(--gold)' : undefined,
                  }}
                >
                  <span className="invite-checkbox" style={{
                    border: `2px solid ${selected.has(f.id) ? 'var(--maroon)' : 'var(--border-ornate)'}`,
                    background: selected.has(f.id) ? 'var(--maroon)' : 'transparent',
                  }}>
                    {selected.has(f.id) ? '\u2713' : ''}
                  </span>
                  <span className="invite-avatar">{f.activeAvatar || f.name.charAt(0).toUpperCase()}</span>
                  <span className="invite-name">{f.name}</span>
                </button>
              ))}
            </div>
            <button className="btn btn-primary w-full" onClick={sendInvites} disabled={selected.size === 0} style={{ fontSize: '0.85rem' }}>
              Send Invite{selected.size > 1 ? 's' : ''} ({selected.size})
            </button>
          </>
        )}
      </div>
    </div>
  );
}
